import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { clientsApi, ApiError } from "@/api";
import type { ClientArchiveCandidate } from "@/types";
import { Button, Spinner, Pill, Toast, useToast } from "@/components/ui";
import { formatMoney } from "@/lib/money";
import "../jobs/archive.css";
import "./clients.css";

/* ─── Archive-readiness ──────────────────────────────────────────────────────
 * The candidate pool is clients whose engagement is wound down (≥1 job, none in
 * active work). A client is "ready to archive" when EVERY job is archived (not
 * merely completed), there are no open tasks/applications, and nothing is
 * outstanding/overdue — the same predicate the clients/archive endpoint
 * enforces (row.ready). When not ready we surface the blocking reason(s). */

function blockers(row: ClientArchiveCandidate): string[] {
  const reasons: string[] = [];
  if (row.completed_job_count > 0) {
    reasons.push(`${row.completed_job_count} job${row.completed_job_count === 1 ? "" : "s"} to archive first`);
  }
  if (row.open_task_count > 0) {
    reasons.push(`${row.open_task_count} open task${row.open_task_count === 1 ? "" : "s"}`);
  }
  if (row.open_application_count > 0) {
    reasons.push(`${row.open_application_count} open application${row.open_application_count === 1 ? "" : "s"}`);
  }
  if (row.outstanding > 0 || row.overdue > 0) {
    const amount = formatMoney(row.outstanding);
    reasons.push(row.overdue > 0 ? `${amount} outstanding · overdue` : `${amount} outstanding`);
  }
  return reasons;
}

/** One-line jobs summary shown on every row. */
function jobsLine(row: ClientArchiveCandidate): string {
  const plural = row.job_count === 1 ? "" : "s";
  if (row.completed_job_count > 0) {
    return `${row.job_count} job${plural} · ${row.archived_job_count} archived, ${row.completed_job_count} to archive`;
  }
  return `${row.job_count} job${plural} · all archived`;
}

/** Archive-clients workbench — the client analogue of the jobs Archive
 *  workbench. Lists clients whose work is wound down (every job completed or
 *  archived) split into "ready to archive" (all jobs archived, no open work,
 *  nothing outstanding) and "not ready" (with the blocking reason). Archiving a
 *  client also archives its Xero contact (best-effort, server-side). */
export function ClientArchiveWorkbench() {
  const { toast, showToast } = useToast();

  const [rows, setRows] = useState<ClientArchiveCandidate[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [archivingIds, setArchivingIds] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmingBulk, setConfirmingBulk] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    clientsApi
      .listArchiveCandidates()
      .then((res) => {
        if (!cancelled) setRows(res.items);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load archive candidates.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  const { ready, notReady } = useMemo(() => {
    const r: ClientArchiveCandidate[] = [];
    const n: ClientArchiveCandidate[] = [];
    for (const row of rows ?? []) {
      (row.ready ? r : n).push(row);
    }
    return { ready: r, notReady: n };
  }, [rows]);

  /** Drop archived clients — once status flips to "archived" they no longer
   *  belong in this candidate view (keeps the ready count + bulk button honest). */
  function removeRows(ids: Iterable<number>) {
    const drop = new Set(ids);
    setRows((prev) => (prev ? prev.filter((row) => !drop.has(row.id)) : prev));
  }

  function xeroNote(archived: boolean, code: string): string {
    if (archived) return "";
    return code === "no_xero_contact_id" ? " (no Xero contact)" : " (Xero contact not archived)";
  }

  async function archiveOne(row: ClientArchiveCandidate) {
    if (bulkBusy || archivingIds.has(row.id)) return;
    setArchivingIds((prev) => new Set(prev).add(row.id));
    setError(undefined);
    try {
      const res = await clientsApi.archiveClient(row.id);
      removeRows([row.id]);
      const note = xeroNote(res.xero_archived, res.xero_error);
      showToast(`Archived ${row.display_name}${note}`, "info");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Could not archive that client.";
      setError(msg);
      showToast(msg, "warn");
    } finally {
      setArchivingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  }

  async function archiveAllReady() {
    setConfirmingBulk(false);
    if (bulkBusy || ready.length === 0) return;
    setBulkBusy(true);
    setError(undefined);

    // Fire together but settle independently so one failure doesn't strand the
    // rest. The browser caps concurrency per host, which also keeps the Xero
    // archive calls from bursting all at once.
    const results = await Promise.allSettled(
      ready.map((row) => clientsApi.archiveClient(row.id).then((res) => ({ id: row.id, xero: res.xero_archived }))),
    );
    const archived: number[] = [];
    let failed = 0;
    let xeroFailed = 0;
    for (const res of results) {
      if (res.status === "fulfilled") {
        archived.push(res.value.id);
        if (!res.value.xero) xeroFailed += 1;
      } else {
        failed += 1;
      }
    }
    removeRows(archived);
    setBulkBusy(false);

    if (failed === 0) {
      const note = xeroFailed > 0 ? ` · ${xeroFailed} Xero contact${xeroFailed === 1 ? "" : "s"} not archived` : "";
      showToast(`Archived ${archived.length} client${archived.length === 1 ? "" : "s"}${note}`, "info");
    } else {
      const msg = `Archived ${archived.length}, ${failed} failed — try again`;
      setError(msg);
      showToast(msg, "warn");
    }
  }

  return (
    <section className="archive-wb client-archive">
      <Link to="/staff/clients" className="archive-wb__back">← Clients</Link>

      <header className="archive-wb__head">
        <div>
          <h1>Archive clients</h1>
          <p className="archive-wb__sub">
            {rows ? (
              <>
                {rows.length} wound-down client{rows.length === 1 ? "" : "s"} · {ready.length} ready to archive
              </>
            ) : (
              "Clients whose jobs are all done — archived, with no open work or outstanding invoices — are ready to archive."
            )}
          </p>
        </div>
        <Button
          variant="primary"
          loading={bulkBusy}
          disabled={bulkBusy || ready.length === 0}
          onClick={() => setConfirmingBulk(true)}
        >
          Archive all ready ({ready.length})
        </Button>
      </header>

      {error ? (
        <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p>
      ) : null}

      {loading ? (
        <div className="archive-wb__loading">
          <Spinner label="Loading archive candidates…" />
        </div>
      ) : !rows || rows.length === 0 ? (
        <p className="archive-wb__empty">No clients are ready to wind down right now.</p>
      ) : (
        <>
          <section className="archive-wb__group">
            <h2 className="archive-wb__group-title">
              Ready to archive <span className="archive-wb__count">{ready.length}</span>
            </h2>
            {ready.length === 0 ? (
              <p className="archive-wb__muted">
                Nothing ready yet — clear the blockers on the clients below (archive their last jobs, close open work, settle invoices).
              </p>
            ) : (
              <div className="archive-table" role="table" aria-label="Ready to archive">
                <div className="archive-table__head archive-table__row--ready" role="row">
                  <span role="columnheader">Client</span>
                  <span role="columnheader">Jobs</span>
                  <span role="columnheader">Email</span>
                  <span role="columnheader">Status</span>
                  <span role="columnheader" className="archive-table__action-col">Action</span>
                </div>
                {ready.map((row) => (
                  <div key={row.id} className="archive-table__row archive-table__row--ready" role="row">
                    <span role="cell" data-label="Client">
                      <Link to={`/staff/clients/${row.id}`} className="archive-table__name-link">
                        {row.display_name}
                      </Link>
                    </span>
                    <span role="cell" data-label="Jobs" className="archive-table__billing">{jobsLine(row)}</span>
                    <span role="cell" data-label="Email" className="archive-table__billing">{row.email || "—"}</span>
                    <span role="cell" data-label="Status">
                      <Pill tone="success">Ready to archive</Pill>
                    </span>
                    <span role="cell" data-label="Action" className="archive-table__action-col">
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={archivingIds.has(row.id)}
                        disabled={bulkBusy || archivingIds.has(row.id)}
                        onClick={() => archiveOne(row)}
                      >
                        Archive now
                      </Button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="archive-wb__group">
            <h2 className="archive-wb__group-title">
              Not ready <span className="archive-wb__count">{notReady.length}</span>
            </h2>
            {notReady.length === 0 ? (
              <p className="archive-wb__muted">Every wound-down client is ready to archive.</p>
            ) : (
              <div className="archive-table" role="table" aria-label="Not ready to archive">
                <div className="archive-table__head archive-table__row--blocked" role="row">
                  <span role="columnheader">Client</span>
                  <span role="columnheader">Jobs</span>
                  <span role="columnheader">Email</span>
                  <span role="columnheader">Why not</span>
                </div>
                {notReady.map((row) => (
                  <Link
                    key={row.id}
                    to={`/staff/clients/${row.id}`}
                    className="archive-table__row archive-table__row--blocked archive-table__row--link"
                    role="row"
                    title="Open the client to resolve the blockers"
                  >
                    <span role="cell" data-label="Client" className="archive-table__name-link">{row.display_name}</span>
                    <span role="cell" data-label="Jobs" className="archive-table__billing">{jobsLine(row)}</span>
                    <span role="cell" data-label="Email" className="archive-table__billing">{row.email || "—"}</span>
                    <span role="cell" data-label="Why not" className="archive-table__reasons">
                      {blockers(row).map((reason) => (
                        <Pill key={reason} tone={reason.includes("overdue") ? "danger" : "neutral"}>
                          {reason}
                        </Pill>
                      ))}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {confirmingBulk ? (
        <div className="archive-wb__confirm-overlay" role="dialog" aria-modal="true">
          <div className="archive-wb__confirm">
            <h2>Archive {ready.length} client{ready.length === 1 ? "" : "s"}?</h2>
            <p>
              Every wound-down client with no open work or outstanding invoices will be archived, and
              its Xero contact archived too. You'll still find them via "Include archived" on the list.
            </p>
            <div className="archive-wb__confirm-actions">
              <Button variant="ghost" onClick={() => setConfirmingBulk(false)} disabled={bulkBusy}>
                Cancel
              </Button>
              <Button variant="primary" onClick={archiveAllReady} loading={bulkBusy}>
                Archive {ready.length}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}
